import { useEffect, useCallback, useRef } from 'react';
import { Box, Typography } from '../../../components/ui';
import { useKnowledgeBase } from '../../../contexts/KnowledgeBaseContext';
import { formatTimeAgo } from '../../../utils/dateUtils';
import { ARTICLE_TYPE_LABELS } from '../../../types/knowledgeBase';
import { StepCard } from '../../KnowledgeBase/components/StepCard';
import { KnownErrorView } from '../../KnowledgeBase/components/KnownErrorView';
import { SOPRunbookView } from '../../KnowledgeBase/components/SOPRunbookView';
import type { Article } from '../../../types/knowledgeBase';
import './ArticlePreviewDrawer.css';

interface ArticlePreviewDrawerProps {
  /** The article to display, or null if closed */
  article: Article | null;
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Called when user clicks backdrop or close button */
  onClose: () => void;
  /** Called when user confirms the article resolved their issue */
  onResolved: (article: Article) => void;
  /** Called when user indicates the article did not resolve their issue */
  onNotResolved: () => void;
}

/**
 * ArticlePreviewDrawer - Slide-in drawer for KB article preview
 *
 * Displays a full article in a drawer overlay without navigating away from
 * the help request form. Includes a resolution prompt with Yes/No options
 * for ticket deflection tracking.
 */
export function ArticlePreviewDrawer({
  article,
  isOpen,
  onClose,
  onResolved,
  onNotResolved,
}: ArticlePreviewDrawerProps) {
  const { incrementViewCount } = useKnowledgeBase();
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Increment view count when article opens
  useEffect(() => {
    if (isOpen && article) {
      incrementViewCount(article.id);
    }
  }, [isOpen, article, incrementViewCount]);

  // Handle escape key to close drawer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden';
      // Focus close button for accessibility
      setTimeout(() => closeButtonRef.current?.focus(), 100);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  // Handle resolution response
  const handleYes = useCallback(() => {
    if (article) {
      onResolved(article);
    }
  }, [article, onResolved]);

  const handleNo = useCallback(() => {
    onNotResolved();
  }, [onNotResolved]);

  if (!isOpen || !article) {
    return null;
  }

  return (
    <div
      className="article-drawer-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="article-drawer-title"
    >
      <div
        ref={drawerRef}
        className="article-drawer"
        role="document"
      >
        {/* Header */}
        <div className="article-drawer__header">
          <button
            ref={closeButtonRef}
            type="button"
            className="article-drawer__close"
            onClick={onClose}
            aria-label="Close article preview"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 5L5 15M5 5l10 10" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="article-drawer__preview-label">Article Preview</span>
        </div>

        {/* Scrollable Content */}
        <div className="article-drawer__content">
          {/* Article Meta */}
          <div className="article-drawer__meta">
            <div className="article-drawer__badges">
              <span className="article-drawer__category">{article.category}</span>
              <span className={`article-drawer__type article-drawer__type--${article.articleType}`}>
                {ARTICLE_TYPE_LABELS[article.articleType]}
              </span>
            </div>
            <Typography
              id="article-drawer-title"
              variant="h5"
              component="h2"
              className="article-drawer__title"
            >
              {article.title}
            </Typography>
            <Typography variant="body2" className="article-drawer__description">
              {article.description}
            </Typography>
            <div className="article-drawer__author">
              <span>By {article.authorName}</span>
              <span className="article-drawer__separator">|</span>
              <span>Updated {formatTimeAgo(article.updatedAt)}</span>
            </div>
          </div>

          {/* Article Content */}
          <div className="article-drawer__body">
            {article.articleContent.type === 'how-to' && (
              <Box className="article-drawer__steps" role="list" aria-label="Steps">
                {article.articleContent.content.steps.map((step) => (
                  <StepCard key={step.id} step={step} />
                ))}
              </Box>
            )}

            {article.articleContent.type === 'known-error' && (
              <KnownErrorView content={article.articleContent.content} />
            )}

            {article.articleContent.type === 'sop-runbook' && (
              <SOPRunbookView content={article.articleContent.content} />
            )}
          </div>
        </div>

        {/* Resolution Prompt Footer */}
        <div className="article-drawer__footer">
          <div className="article-drawer__resolution">
            <div className="article-drawer__resolution-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v.01M12 12v4" strokeLinecap="round" />
              </svg>
            </div>
            <div className="article-drawer__resolution-content">
              <span className="article-drawer__resolution-question">
                Did this article resolve your issue?
              </span>
              <span className="article-drawer__resolution-hint">
                Let us know so we can improve our knowledge base
              </span>
            </div>
          </div>

          <div className="article-drawer__actions">
            <button
              type="button"
              className="article-drawer__action article-drawer__action--yes"
              onClick={handleYes}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 9l4 4 6-8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Yes, solved
            </button>
            <button
              type="button"
              className="article-drawer__action article-drawer__action--no"
              onClick={handleNo}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 5l8 8M13 5l-8 8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              No, still need help
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
